import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LifeBuoy, Send, Check, ArrowLeft, BookMarked, Loader2 } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function Support() {
  const { user } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [responseTime, setResponseTime] = useState("");

  useEffect(() => {
    api.get("/support/config").then((r) => setResponseTime(r.data.response_time_text)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) {
      setName((n) => n || user.name);
      setEmail((e) => e || user.email);
    }
  }, [user]);

  const submit = async () => {
    if (!name.trim() || !email.trim() || !message.trim()) {
      toast.error("Name, email and message are required");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/support/submit", { name, email, subject, message });
      setSubmitted(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <header className="px-6 sm:px-8 lg:px-12 py-5 border-b-2 border-slate-900 bg-white flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="support-home-link">
          <div className="w-10 h-10 bg-yellow-300 border-2 border-slate-900 rounded-md grid place-items-center" style={{ boxShadow: "3px 3px 0 0 #0F172A" }}>
            <BookMarked className="w-5 h-5" strokeWidth={2.5} />
          </div>
          <span className="font-display text-2xl font-black">Studict</span>
        </Link>
        <Link to="/" className="nb-btn-secondary inline-flex items-center gap-2 text-sm">
          <ArrowLeft className="w-4 h-4" strokeWidth={2.5}/> Home
        </Link>
      </header>

      <main className="max-w-2xl mx-auto px-6 sm:px-8 py-10 lg:py-16">
        {submitted ? (
          <section className="nb-card p-8 text-center" data-testid="support-success">
            <div className="w-16 h-16 mx-auto bg-green-300 border-2 border-slate-900 rounded-full grid place-items-center" style={{ boxShadow: "4px 4px 0 0 #0F172A" }}>
              <Check className="w-8 h-8" strokeWidth={3}/>
            </div>
            <h1 className="font-display text-3xl font-black mt-5">Got it. Thanks!</h1>
            <p className="text-base font-medium text-slate-700 mt-3">{submitted.message}</p>
            <div className="nb-card-sm p-4 mt-6 inline-block bg-yellow-100">
              <p className="text-xs font-bold uppercase tracking-widest text-slate-600">Your ticket number</p>
              <p className="font-display text-2xl font-black mt-1" data-testid="ticket-number">{submitted.ticket_number}</p>
            </div>
            <p className="text-xs text-slate-500 mt-5">Save this number in case you need to follow up.</p>
            <div className="flex gap-3 justify-center mt-6">
              <Link to="/" className="nb-btn-primary inline-flex items-center gap-2">Back to home</Link>
              <button onClick={() => { setSubmitted(null); setSubject(""); setMessage(""); }} className="nb-btn-secondary">Send another</button>
            </div>
          </section>
        ) : (
          <>
            <header className="mb-6">
              <span className="nb-tag" style={{ background: "#FDE047" }}>Support</span>
              <h1 className="font-display text-4xl sm:text-5xl font-black mt-4">How can we help?</h1>
              <p className="text-base font-medium text-slate-700 mt-3">
                Stuck on something? Found a bug? Just want to suggest a feature? Drop us a line and we'll get back to you.
                {responseTime && <span className="block mt-2 text-sm text-slate-500"><strong>Typical response:</strong> {responseTime}</span>}
              </p>
            </header>

            <section className="nb-card p-6 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs uppercase font-bold tracking-widest">Your name</label>
                  <input data-testid="support-name" value={name} onChange={(e) => setName(e.target.value)} className="nb-input mt-1" placeholder="Alex"/>
                </div>
                <div>
                  <label className="text-xs uppercase font-bold tracking-widest">Email</label>
                  <input data-testid="support-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="nb-input mt-1" placeholder="alex@school.edu"/>
                </div>
              </div>
              <div>
                <label className="text-xs uppercase font-bold tracking-widest">Subject (optional)</label>
                <input data-testid="support-subject" value={subject} onChange={(e) => setSubject(e.target.value)} className="nb-input mt-1" placeholder="Something doesn't work"/>
              </div>
              <div>
                <label className="text-xs uppercase font-bold tracking-widest">Message</label>
                <textarea data-testid="support-message" rows={6} value={message} onChange={(e) => setMessage(e.target.value)} className="nb-input mt-1" placeholder="Tell us what's happening, what you expected, and what device you're on."/>
              </div>
              <button onClick={submit} disabled={busy} data-testid="support-submit-btn" className="nb-btn-primary inline-flex items-center gap-2 w-full sm:w-auto justify-center">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2.5}/> : <Send className="w-4 h-4" strokeWidth={2.5}/>}
                {busy ? "Sending…" : "Send to support"}
              </button>
            </section>
          </>
        )}
      </main>

      <footer className="px-6 sm:px-8 py-8 border-t-2 border-slate-900 bg-white mt-10">
        <p className="text-xs font-medium text-slate-500 inline-flex items-center gap-2">
          <LifeBuoy className="w-4 h-4" strokeWidth={2.5}/>
          Real humans read every ticket. Be kind, be specific, and we'll do our best.
        </p>
      </footer>
    </div>
  );
}
